"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader } from "lucide-react";
function CoordinateCorners() {
  return <>
      <span aria-hidden="true" className="pointer-events-none absolute top-0 left-0 w-2 h-2 border-t border-l border-primary" />
      <span aria-hidden="true" className="pointer-events-none absolute top-0 right-0 w-2 h-2 border-t border-r border-primary" />
      <span aria-hidden="true" className="pointer-events-none absolute bottom-0 left-0 w-2 h-2 border-b border-l border-primary" />
      <span aria-hidden="true" className="pointer-events-none absolute bottom-0 right-0 w-2 h-2 border-b border-r border-primary" />
    </>;
}
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="relative p-8 border border-border bg-card">
        <CoordinateCorners />
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-secondary mb-3">
          § SOLICITUD RECIBIDA
        </p>
        <p className="text-foreground font-default text-2xl mb-6 leading-tight font-semibold">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground font-mono uppercase tracking-[0.04em] text-[13px] h-auto px-8 py-3">
          Enviar Nueva Solicitud
        </Button>
      </div>;
  }
  const inputClasses = "h-auto bg-transparent border-0 border-b border-border rounded-md px-0 py-3 text-foreground placeholder:text-muted-foreground shadow-none focus-visible:ring-0 focus-visible:border-secondary transition-colors";
  const labelClasses = "font-mono uppercase tracking-[0.15em] text-[11px] text-muted-foreground font-medium";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Juan Pérez" {...field} className={inputClasses} />
              </FormControl>
              <FormDescription className="font-mono text-[11px] tracking-[0.08em] text-muted-foreground/60">
                Nombre y apellidos del solicitante
              </FormDescription>
              <FormMessage className="font-mono text-[11px] tracking-[0.08em]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El correo es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Correo inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="contacto@empresa.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-[11px] tracking-[0.08em]" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "El teléfono es obligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Teléfono de Contacto</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="55 1234 5678" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="font-mono text-[11px] tracking-[0.08em]" />
            </FormItem>} />

        <FormField control={control} name="service_type" rules={{
        required: "Seleccione un servicio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClasses}>Servicio de Interés</FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <FormControl>
                  <SelectTrigger className={"h-auto bg-transparent border-0 border-b border-border rounded-md px-0 py-3 text-foreground shadow-none focus-visible:ring-0 focus-visible:border-secondary data-[placeholder]:text-muted-foreground/40 w-full"}>
                    <SelectValue placeholder="Seleccione un servicio" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="Seguridad Intramuros">
                    Seguridad Intramuros
                  </SelectItem>
                  <SelectItem value="Monitoreo y Videovigilancia">
                    Monitoreo y Videovigilancia
                  </SelectItem>
                  <SelectItem value="Rastreo GPS">
                    Rastreo GPS
                  </SelectItem>
                  <SelectItem value="Protección Ejecutiva">
                    Protección Ejecutiva
                  </SelectItem>
                  <SelectItem value="Otro">Otro</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="font-mono text-[11px] tracking-[0.08em]" />
            </FormItem>} />

        <FormField control={control} name="preferred_date" rules={{
        required: "Seleccione una fecha"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className={labelClasses}>Fecha para Diagnóstico</FormLabel>
              <FormControl>
                <Input type="date" {...field} className={inputClasses} />
              </FormControl>
              <FormDescription className="font-mono text-[11px] tracking-[0.08em] text-muted-foreground/60">
                Seleccione el día tentativo para la evaluación técnica
              </FormDescription>
              <FormMessage className="font-mono text-[11px] tracking-[0.08em]" />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className={labelClasses}>
                Detalles Adicionales
              </FormLabel>
              <FormControl>
                <Textarea rows={5} placeholder="Describa brevemente sus necesidades de seguridad o infraestructura actual..." {...field} className={"bg-transparent border-0 border-b border-border rounded-md px-0 py-3 text-foreground placeholder:text-muted-foreground/40 shadow-none focus-visible:ring-0 focus-visible:border-secondary resize-none transition-colors"} />
              </FormControl>
              <FormMessage className="font-mono text-[11px] tracking-[0.08em]" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/40 bg-destructive/10">
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-destructive">
            {submitError}
          </p>
        </div>}

      <div className="pt-6 border-t border-border relative">
        <span aria-hidden="true" className="absolute -top-[3px] right-0 w-1.5 h-1.5 rounded-md bg-secondary" />
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
            Respuesta técnica en menos de 24 horas
          </p>
          <Button type="submit" disabled={isSubmitting} className="rounded-md bg-secondary text-secondary-foreground hover:bg-foreground hover:text-background hover:-translate-y-0.5 shadow-md font-mono uppercase tracking-[0.04em] text-[13px] h-auto px-8 py-4 transition-all duration-200">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Procesando...
              </> : "Solicitar Diagnóstico"}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function SolicitarDiagnSticoTecnolGico() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const contactRows = [{
    label: "Corporativo",
    value: "(55) 1234-5678"
  }, {
    label: "Email",
    value: "contacto@grupoposeidon.com.mx"
  }, {
    label: "Atención",
    value: "24/7 Monitoreo"
  }];
  return <section data-section-id="2149" id="solicitar-diagnóstico-tecnológico" className="relative bg-background py-24 md:py-32 border-t border-border">
      <div className="container mx-auto px-5 md:px-10 lg:px-18 max-w-[1440px]">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <div className="flex items-center gap-3 mb-10">
                <span className="block w-6 h-px bg-secondary" />
                <p className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium">
                  § N° 06 — Contacto
                </p>
              </div>

              <h2 className="font-default text-foreground font-bold leading-[1.1] tracking-tight mb-8 text-4xl md:text-5xl lg:text-6xl">
                Proteja su{" "}
                <span className="italic font-light relative inline-block">
                  patrimonio
                  <span aria-hidden="true" className="absolute left-0 -bottom-1 h-px w-full bg-secondary" />
                </span>
                .
              </h2>

              <p className="text-muted-foreground text-lg leading-[1.6] mb-12 max-w-md font-default">
                Inicie su protocolo de seguridad con un diagnóstico tecnológico integral. Nuestro equipo de expertos analizará sus vulnerabilidades para diseñar una estrategia de protección a medida.
              </p>

              <div className="space-y-0">
                {contactRows.map((row, i) => <div key={i} data-index={i} className="relative py-5 border-t border-border last:border-b">
                    <span aria-hidden="true" className="absolute -top-[3px] left-0 w-1.5 h-1.5 rounded-md bg-secondary" />
                    <div className="flex items-baseline justify-between gap-6">
                      <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-medium">
                        {row.label}
                      </span>
                      <span className="text-foreground font-mono text-sm tracking-[0.04em] text-right">
                        {row.value}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative bg-card border border-border p-8 md:p-12 rounded-md shadow-xl">
              <CoordinateCorners />
              <FormProvider formKey="diagnostic_form_technology" formId="diagnostic_form_technology" sectionName="solicitar-diagnóstico-tecnológico" defaultValues={{
              name: "",
              email: "",
              phone: "",
              service_type: "",
              preferred_date: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              service_type: {
                required: true
              },
              preferred_date: {
                required: true
              }
            }} submitText="Solicitar Diagnóstico" successMessage="Gracias. Un consultor de seguridad de Grupo Poseidón se pondrá en contacto con usted en menos de 24 horas." errorMessage="No pudimos procesar su solicitud. Por favor intente de nuevo o llámenos directamente.">
                <ConsultationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}